import type {LucideIcon} from 'lucide-react';
import {
  Activity,
  Building2,
  Code2,
  Fuel,
  LayoutDashboard,
  Link2,
  Lock,
  Plug,
  RefreshCw,
  Send,
  ShieldCheck,
  ShoppingCart,
  Truck,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';

export type NavItem = {href: string; label: string};
export type FeatureItem = {title: string; items: string[]};
export type CardItem = {title: string; description: string; items: string[]; icon: LucideIcon};
export type PillarItem = {
  id: string;
  title: string;
  description: string;
  capabilities: string[];
  icon: LucideIcon;
};
export type FlowStep = {step: string; title: string; description: string};
export type UseCaseItem = {title: string; description: string; icon: LucideIcon};
export type SecurityCard = {title: string; items: string[]; icon: LucideIcon};

export const heroContent = {
  badge: 'Infraestructura de pagos empresarial',
  title: 'Cobra, concilia y dispersa dinero sin depender de procesos manuales.',
  description:
    'Kobra conecta tus cobros con tu operación para que sepas quién pagó, cuánto pagó y qué debe ocurrir después.',
};

export const socialProofContent = {
  title: 'Infraestructura financiera para operaciones que necesitan control y trazabilidad',
  trustSignals: [
    'Pagos trazables',
    'Conciliación automática',
    'Dispersión controlada',
    'Integración vía API',
  ],
  /** Activar cuando haya métricas reales para mostrar */
  stats: [] as {value: string; label: string}[],
};

export const usageModesContent = {
  id: 'modos',
  title: 'Kobra se adapta a tu forma de operar',
  description:
    'Utiliza la plataforma directamente o incorpora sus capacidades financieras dentro de la infraestructura actual de tu empresa.',
  modes: [
    {
      title: 'Dashboard Kobra',
      description:
        'Administra operaciones desde una interfaz centralizada sin necesidad de construir tu propio panel.',
      items: ['Cobros', 'Transacciones', 'Movimientos', 'Dispersiones', 'Permisos'],
      icon: LayoutDashboard,
    },
    {
      title: 'API para productos digitales',
      description:
        'Integra Kobra dentro de tu SaaS, marketplace, ecommerce, aplicación o plataforma.',
      items: ['Cobros', 'Webhooks', 'Idempotencia', 'Dispersiones', 'Metadata'],
      icon: Code2,
    },
    {
      title: 'Integración con tus sistemas',
      description: 'Conecta Kobra con la operación tecnológica de tu empresa.',
      items: ['ERP', 'Ecommerce', 'SaaS', 'Apps móviles', 'Sistemas propios'],
      icon: Plug,
    },
  ] satisfies CardItem[],
};

export const capabilitiesContent = {
  id: 'producto',
  title: 'Una infraestructura para mover dinero con control',
  description:
    'Centraliza la creación de cobros, la confirmación de pagos, los eventos operativos y los pagos salientes desde una misma plataforma.',
  pillars: [
    {
      id: 'recauda',
      title: 'Recauda',
      description: 'Crea experiencias de cobro adaptadas a tu empresa o producto digital.',
      capabilities: [
        'Links de pago',
        'Cobros vía API',
        'Referencias personalizadas',
        'Confirmación de transacciones',
      ],
      icon: Link2,
    },
    {
      id: 'valida',
      title: 'Valida',
      description:
        'Verifica la información de cada transacción antes de notificar a tus sistemas.',
      capabilities: [
        'Referencia y monto',
        'Confirmación del proveedor',
        'Control de duplicados',
        'Estados predecibles',
      ],
      icon: ShieldCheck,
    },
    {
      id: 'automatiza',
      title: 'Automatiza',
      description: 'Conecta cada evento financiero con las reglas de tu producto.',
      capabilities: ['Webhooks', 'Reintentos', 'Callbacks', 'Actualización de estados'],
      icon: Zap,
    },
    {
      id: 'dispersa',
      title: 'Dispersa',
      description:
        'Programa y controla pagos salientes mediante proveedores financieros integrados.',
      capabilities: [
        'Dispersiones individuales y masivas',
        'Pagos a proveedores y aliados',
        'Programación de operaciones',
        'Comprobantes',
      ],
      icon: Send,
    },
  ] satisfies PillarItem[],
};

export const transactionContextContent = {
  id: 'contexto',
  title: 'Cada transacción conserva su contexto',
  description:
    'Kobra utiliza referencias, metadata y eventos para que tus sistemas conozcan el estado de cada operación sin depender de revisiones manuales.',
  sent: {
    title: 'Información enviada al crear el cobro',
    items: [
      'Referencia: ORD-1029',
      'Cliente externo: CLI-501',
      'Valor: $1.250.000',
      'Tipo de operación: Compra',
      'Callback configurado',
    ],
  },
  received: {
    title: 'Información devuelta por Kobra',
    items: [
      'Identificador de transacción',
      'Estado y valor confirmado',
      'Fecha y proveedor',
      'Referencia original',
      'Resultado del webhook',
    ],
  },
  note: 'Kobra confirma y notifica la transacción. El sistema integrado decide cómo aplicar el resultado dentro de su operación.',
};

export const operationFlowContent = {
  id: 'como-funciona',
  title: 'Del cobro al siguiente movimiento',
  steps: [
    {
      step: '01',
      title: 'Creas la operación',
      description: 'Desde el dashboard, la API o un sistema integrado.',
    },
    {
      step: '02',
      title: 'Kobra genera el cobro',
      description: 'Se crea una referencia y se habilita el mecanismo de pago correspondiente.',
    },
    {
      step: '03',
      title: 'El cliente paga',
      description: 'La transacción es procesada mediante el proveedor financiero disponible.',
    },
    {
      step: '04',
      title: 'Kobra confirma el resultado',
      description: 'Se valida el estado, la referencia, el valor y la información recibida.',
    },
    {
      step: '05',
      title: 'Tus sistemas reciben el evento',
      description: 'Kobra comunica el resultado mediante API, callback o webhook.',
    },
    {
      step: '06',
      title: 'Continúa el flujo financiero',
      description:
        'Tu empresa puede actualizar su operación, calcular reglas internas o programar una dispersión.',
    },
  ] satisfies FlowStep[],
};

export const useCasesContent = {
  id: 'casos',
  title: 'Casos de uso',
  description:
    'Kobra se integra en distintos modelos operativos donde necesitas recaudar, confirmar y mover dinero con control.',
  cases: [
    {
      title: 'Marketplaces y plataformas',
      description:
        'Recibe pagos, conserva referencias por operación y programa pagos a vendedores, comercios o aliados.',
      icon: ShoppingCart,
    },
    {
      title: 'SaaS',
      description:
        'Incorpora cobros, estados de pago y eventos financieros directamente dentro de tu producto.',
      icon: Code2,
    },
    {
      title: 'POS y ecommerce',
      description:
        'Genera pagos, confirma órdenes y recibe actualizaciones de transacciones mediante webhooks.',
      icon: Building2,
    },
    {
      title: 'Empresas de servicios',
      description:
        'Crea cobros para clientes, controla sus estados y programa pagos a proveedores o contratistas.',
      icon: Wrench,
    },
    {
      title: 'Logística y rutas',
      description:
        'Gestiona cobros operativos y pagos salientes para conductores, aliados o personal de campo.',
      icon: Truck,
    },
    {
      title: 'Electrolineras y servicios por consumo',
      description:
        'Genera un cobro asociado a una sesión o consumo y comunica el resultado al sistema encargado de activar o finalizar el servicio.',
      icon: Fuel,
    },
    {
      title: 'Plataformas con múltiples participantes',
      description:
        'Distribuye información y reglas de una operación entre diferentes participantes y ejecuta pagos cuando la funcionalidad esté habilitada.',
      icon: Users,
    },
  ] satisfies UseCaseItem[],
};

export const developersContent = {
  id: 'developers',
  title: 'Construida para integrarse sin fricción',
  description:
    'Endpoints consistentes, estados predecibles y eventos claros para incorporar capacidades financieras dentro de tu producto.',
  features: [
    'API REST',
    'Autenticación por token',
    'Idempotencia',
    'Webhooks firmados (HMAC)',
    'Reintentos',
    'Logs',
    'Metadata',
  ],
  integrationNote: 'Ejemplos de integración en múltiples lenguajes.',
};

export const securityContent = {
  id: 'seguridad',
  title: 'Control y seguridad en cada operación',
  description:
    'Kobra protege los accesos con tokens, verifica cada webhook con firma HMAC y registra el recorrido completo de la transacción.',
  highlights: ['Tokens por ambiente', 'Firma HMAC', 'Idempotencia', 'Logs trazables'],
  cards: [
    {
      title: 'Accesos',
      items: ['Credenciales por ambiente', 'Tokens con expiración', 'Roles y permisos'],
      icon: Lock,
    },
    {
      title: 'Idempotencia',
      items: ['Prevención de duplicados', 'Reintentos seguros', 'Claves de idempotencia'],
      icon: RefreshCw,
    },
    {
      title: 'Webhooks verificables',
      items: ['Firma HMAC', 'Validación del origen', 'Reintentos controlados'],
      icon: ShieldCheck,
    },
    {
      title: 'Trazabilidad operativa',
      items: ['Historial de estados', 'Registro de eventos', 'Logs con identificadores'],
      icon: Activity,
    },
  ] satisfies SecurityCard[],
};

export const documentationContent = {
  id: 'docs',
  title: 'Documentación para construir más rápido',
  description:
    'Consulta guías, referencias y ejemplos para integrar recaudos, eventos y pagos salientes dentro de tu producto.',
  topics: [
    'Crear un cobro',
    'Generar un link de pago',
    'Configurar webhooks',
    'Utilizar idempotencia',
    'Crear una dispersión',
    'Manejar errores',
  ],
};

export const finalCtaContent = {
  title: 'Construye tu operación financiera sobre Kobra',
  description:
    'Crea cobros, confirma transacciones, automatiza eventos y controla pagos salientes desde una infraestructura preparada para empresas y productos digitales.',
  complement:
    'Utiliza Kobra directamente o intégrala con los sistemas que tu empresa ya utiliza.',
};

export const footerContent = {
  tagline:
    'Infraestructura para recaudar, validar, controlar y dispersar dinero. Kobra conecta los canales de pago con los sistemas y reglas operativas de tu empresa.',
  productLinks: [
    {href: '#producto', label: 'Recaudos'},
    {href: '#producto', label: 'Validación de transacciones'},
    {href: '#producto', label: 'Automatizaciones'},
    {href: '#producto', label: 'Dispersiones'},
    {href: '#casos', label: 'Casos de uso'},
  ],
  developerLinks: [
    {href: '#docs', label: 'Documentación'},
    {href: '#developers', label: 'Referencia API'},
    {href: '#developers', label: 'Webhooks'},
    {href: '#seguridad', label: 'Seguridad'},
  ],
  companyLinks: [
    {href: '#', label: 'Nosotros'},
    {href: '#contacto', label: 'Contacto'},
    {href: '#', label: 'Privacidad'},
    {href: '#', label: 'Términos'},
    {href: '#', label: 'Tratamiento de datos'},
  ],
};
